import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ClientDiscountService from '../../services/ClientDiscountService';
import DateField from '../common/DateField';
import { isValidDateValue } from '../common/PolishDateInput';
import { displayDate, displayDateTime } from '../common/datePresentation';
import { canReadPayments } from './rbac';
import { discountToday, latestDiscountVersions, useDiscountPolicy } from './discountManagement';

export default function DiscountTypesSettings({ user, service }) {
  const { t: tr } = useTranslation();
  const t = key => tr(`billing:discounts.${key}`);
  const allowed = canReadPayments(user);
  const policy = useDiscountPolicy(() => service.getTypes(), allowed);
  const [draft, setDraft] = useState(null);
  const types = policy.data ? latestDiscountVersions(policy.data.versions, 'typeId') : [];
  const change = (key, value) => setDraft(current => ({ ...current, [key]: value }));
  const valid = draft && (draft.intent === 'end' ? isValidDateValue(draft.endedFrom) && draft.endedFrom > draft.effectiveFrom : (
    draft.name.trim() && draft.percent !== '' && Number(draft.percent) >= 0 && Number(draft.percent) <= 100 &&
    isValidDateValue(draft.effectiveFrom) && (!draft.originalFrom || draft.effectiveFrom > draft.originalFrom)
  ));
  const submit = async (event) => {
    event.preventDefault();
    if (!valid) return;
    const payload = {
      intent: draft.intent,
      expectedRevision: policy.data.revision,
      ...(draft.typeId ? { typeId: draft.typeId } : {}),
      ...(draft.intent === 'end' ? { endedFrom: draft.endedFrom } : {
        name: draft.name.trim(),
        defaultPercentBasisPoints: Math.round(Number(draft.percent) * 100),
        effectiveFrom: draft.effectiveFrom,
        requiresPrepayment: draft.requiresPrepayment,
        requiresValidTo: draft.requiresValidTo,
      }),
    };
    if (await policy.save(service.saveType, payload)) setDraft(null);
  };
  if (!allowed) return null;
  return (
    <section
      className="card mb-3"
      aria-label={t('typesTitle')}
    >
      <div
        className="card-body"
      >
        <h5
          className="card-title"
        >{t('typesTitle')}
        </h5>
        <p>{t('defaultHint')}</p>
        {policy.error && (
        <div
          role="alert"
          className="alert alert-danger"
        >{t(policy.error)}
        </div>
        )}
        {(policy.error || !policy.data) && (
        <button
          type="button"
          className="btn manage-button manage-teal-button"
          disabled={policy.busy}
          onClick={policy.reload}
        >{t('reload')}
        </button>
        )}
        {policy.data && !types.length && <p>{t('emptyTypes')}</p>}
        {types.map(type => (
          <div
            key={type.typeId}
            className="border-bottom py-2"
          >
            <strong>{type.name} · {type.defaultPercentBasisPoints / 100}%</strong>
            <p>{t('from')}: {displayDate(type.effectiveFrom)}{type.endedFrom && ` · ${t('endedFrom')}: ${displayDate(type.endedFrom)}`}</p>
            <button
              type="button"
              className="btn manage-button manage-teal-button mr-2"
              disabled={policy.busy || !!draft || !!type.endedFrom}
              onClick={() => setDraft({
                ...type,
                requiresValidTo: !!type.requiresValidTo,
                requiresPrepayment: !!type.requiresPrepayment,
                intent: 'change',
                originalFrom: type.effectiveFrom,
                effectiveFrom: '',
                percent: String(type.defaultPercentBasisPoints / 100),
              })}
            >{t('edit')}
            </button>
            <button
              type="button"
              className="btn manage-button manage-teal-button"
              disabled={policy.busy || !!draft || !!type.endedFrom}
              onClick={() => setDraft({ ...type, intent: 'end', endedFrom: '' })}
            >{t('endGlobal')}
            </button>
          </div>
        ))}
        {policy.data && (
        <details
          className="my-3"
        ><summary>{t('history')}</summary>{policy.data.versions.map(type => (
          <p
            key={`${type.typeId}-${type.version}`}
          >{type.name} · {t('version')} {type.version} · {type.defaultPercentBasisPoints / 100}% · {displayDate(type.effectiveFrom)}
            {type.endedFrom && ` · ${t('endedFrom')}: ${displayDate(type.endedFrom)}`} · {type.actorId} · {displayDateTime(type.recordedAt)}
          </p>
        ))}
        </details>
        )}
        {policy.data && !draft && (
        <button
          type="button"
          className="btn manage-button manage-red-button"
          disabled={policy.busy}
          onClick={() => setDraft({
            intent: 'create', name: '', percent: '', effectiveFrom: discountToday(), requiresPrepayment: false, requiresValidTo: false,
          })}
        >{t('addType')}
        </button>
        )}
        {draft && (
        <form
          onSubmit={submit}
          className="border-top pt-3 mt-3"
        >
          <fieldset
            disabled={policy.busy}
          >
            {draft.intent === 'end' ? (
              <>
                <p>{t('endHint')}</p>
                <label
                  className="d-block"
                  htmlFor="DiscountTypesSettings-field-1"
                >
                  {t('endedFrom')}
                  <DateField
                    id="DiscountTypesSettings-field-1"
                    aria-label={t('endedFrom')}
                    className="form-control"
                    type="date"
                    required
                    value={draft.endedFrom}
                    onChange={event => change('endedFrom', event.target.value)}
                  />
                </label>
              </>
            ) : (
              <>
                <label
                  className="d-block"
                  htmlFor="DiscountTypesSettings-field-2"
                >
                  {t('name')}
                  <input
                    id="DiscountTypesSettings-field-2"
                    className="form-control"
                    required
                    maxLength={100}
                    value={draft.name}
                    onChange={event => change('name', event.target.value)}
                  />
                </label>
                <label
                  className="d-block"
                  htmlFor="DiscountTypesSettings-field-3"
                >
                  {t('defaultPercent')}
                  <input
                    id="DiscountTypesSettings-field-3"
                    className="form-control"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    required
                    value={draft.percent}
                    onChange={event => change('percent', event.target.value)}
                  />
                </label>
                <label
                  className="d-block"
                  htmlFor="DiscountTypesSettings-field-4"
                >
                  {t('effectiveFrom')}
                  <DateField
                    id="DiscountTypesSettings-field-4"
                    aria-label={t('effectiveFrom')}
                    className="form-control"
                    type="date"
                    required
                    value={draft.effectiveFrom}
                    onChange={event => change('effectiveFrom', event.target.value)}
                  />
                </label>
                <label
                  className="d-block"
                  htmlFor="DiscountTypesSettings-field-5"
                >

                  <input
                    id="DiscountTypesSettings-field-5"
                    type="checkbox"
                    checked={draft.requiresValidTo}
                    onChange={event => change('requiresValidTo', event.target.checked)}
                  /> {t('requireEnd')}
                </label>
                <label
                  className="d-block"
                  htmlFor="DiscountTypesSettings-field-6"
                >

                  <input
                    id="DiscountTypesSettings-field-6"
                    type="checkbox"
                    checked={draft.requiresPrepayment}
                    onChange={event => change('requiresPrepayment', event.target.checked)}
                  /> {t('requirePrepayment')}
                </label>
              </>
            )}
            <button
              type="button"
              className="btn manage-button manage-teal-button mr-2"
              onClick={() => setDraft(null)}
            >{tr('common:cancel')}
            </button>
            <button
              type="submit"
              className="btn manage-button manage-red-button"
              disabled={!valid || ['conflict', 'refresh'].includes(policy.error)}
            >{tr('common:save')}
            </button>
          </fieldset>
        </form>
        )}
      </div>
    </section>
  );
}
DiscountTypesSettings.propTypes = { user: PropTypes.object, service: PropTypes.object };
DiscountTypesSettings.defaultProps = { user: null, service: ClientDiscountService };
